import useSWRMutation from 'swr/mutation';
import { getAccessToken, useHasAccessToken } from '@/auth/api';
import {
  useBackend,
  useBackendMutation,
  type WealthCoin,
  type WealthNetwork,
  type WealthPair,
} from '@/backend';
import { ACCOUNT_API_BASE_URL } from '@/base/constants';

export const useCoins = () =>
  useBackend(
    'wealth:get:coins/',
    {},
    {
      swrOptions: {
        dedupingInterval: 1000 * 60,
        refreshInterval: 1000 * 60,
      },
    },
  );

export const useCoin = (coin?: string | WealthCoin) => {
  const slug = typeof coin === 'object' ? coin.slug : coin?.toUpperCase();

  const coins = useCoins();

  const data =
    typeof coin === 'object'
      ? coin
      : coins.data?.find((x) => x.slug.toUpperCase() === slug);

  return {
    ...coins,
    mutate: null,
    data,
  };
};

export const usePairs = () =>
  useBackend(
    'wealth:get:pairs/',
    {},
    {
      swrOptions: {
        dedupingInterval: 1000 * 60,
        refreshInterval: 1000 * 60,
      },
    },
  );

export const usePair = (pair?: string | WealthPair) => {
  const slug = typeof pair === 'object' ? pair.slug : pair?.toUpperCase();

  const pairs = usePairs();

  const data =
    typeof pair === 'object'
      ? pair
      : pairs.data?.find((x) => x.slug.toUpperCase() === slug);

  return {
    ...pairs,
    mutate: null,
    data,
  };
};

export const useNetworks = () =>
  useBackend(
    'wealth:get:networks/',
    {},
    {
      swrOptions: {
        dedupingInterval: 1000 * 60 * 60 * 24,
        refreshInterval: 1000 * 60 * 60 * 24,
      },
    },
  );

export const useNetwork = (network?: string | WealthNetwork) => {
  const slug =
    typeof network === 'object' ? network.slug : network?.toUpperCase();

  const networks = useNetworks();

  const data =
    typeof network === 'object'
      ? network
      : networks.data?.find((x) => x.slug.toUpperCase() === slug);

  return {
    ...networks,
    mutate: null,
    data,
  };
};

export type UploadFileResponse = {
  file: string;
};

export type UploadBucket = 'kyc' | 'p2p';

export const useUpload = (bucket: UploadBucket) => {
  return useSWRMutation(
    'upload',
    (
      _: unknown,
      {
        arg,
      }: {
        arg: {
          file: File;
          onProgress?: (progress: number) => void;
        };
      },
    ) => {
      const formData = new FormData();
      formData.append('file', arg.file);
      formData.append('metadata', JSON.stringify({ bucket }));
      return new Promise<UploadFileResponse>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const progress = (e.loaded / e.total) * 100;
            arg?.onProgress?.(Math.min(progress, 100));
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(
                xhr.responseText,
              ) as UploadFileResponse;
              resolve(response);
            } catch {
              reject(new Error('Invalid response'));
            }
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Upload failed'));
        });

        xhr.open('POST', `${ACCOUNT_API_BASE_URL}/api/v1/account/upload/`);
        xhr.setRequestHeader('Accept', '*/*');
        const accessToken = getAccessToken();
        if (accessToken) {
          xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
        }
        xhr.send(formData);
      });
    },
  );
};

export const useUserStorage = (key: string) =>
  useBackend(
    'account:get:user-storage/{slug}',
    {
      slug: key,
    },
    {
      enabled: useHasAccessToken(),
    },
  );

export const useSaveUserStorage = () => {
  return useBackendMutation('account:post:user-storage/{slug}', {
    revalidate: ['account:get:user-storage/{slug}'],
    enabled: useHasAccessToken(),
  });
};

export const useDeleteUserStorage = () =>
  useBackendMutation('account:delete:user-storage/{slug}', {
    revalidate: ['account:get:user-storage/{slug}'],
    enabled: useHasAccessToken(),
  });
